import { useMemo, useRef } from 'react';
import { cn, ButtonTabs, RawHtml } from '@divlab/divanui';

import LayoutMain from '@Layouts/LayoutMain';
import useMedias from '@Hooks/useMedias';
import GallerySection from '@Components/GallerySection';
import ProductsSection from '@Components/ProductsSection';
import ArticleCard from '@Components/ArticleCard';
import VideoPlayer from '@Components/VideoPlayer';
import usePage from '@Queries/usePage';
import useTranslation from '@Queries/useTranslation';
import Page from '@Components/Page';
import PageTopBar from '@Components/PageTopBar';
import Subscription from './elements/Subscription';
import ArticlesNavButton from './elements/ArticlesNavButton';
import Lead from './elements/Lead';
import PhotoSlider from './elements/PhotoSlider';
import TwoColumnsContent from './elements/TwoColumnsContent';
import InteriorImageContainer from './elements/InteriorImageContainer';
import InstagramPost from './elements/InstagramPost';
import SingleContent from './elements/SingleContent';
import FullscreenSlider from './elements/FullscreenSlider';
import Page404 from '@Pages/Page404';
import IconLeft from './elements/IconLeft';
import IconRight from './elements/IconRight';
import styles from './PageWikiArticle.module.css';
import './PageWikiArticle.raw.css';

import type { PageWikiArticleData } from './typings';
import type { FC } from 'react';

const PageWikiArticle: FC = () => {
  const { data: page } = usePage<PageWikiArticleData>();
  const {
    title,
    prevArticle,
    nextArticle,
    tags,
    content,
    relatedArticles,
    currentRubric,
    mainImage,
    id,
  } = page;
  const { isMobileM, isDesktop } = useMedias();
  const { t } = useTranslation();
  const refGrid = useRef<HTMLDivElement>(null);

  const tabs = useMemo(() => {
    return tags.map((tag) => ({ label: tag.title, id: tag.title, link: tag.link }));
  }, [tags]);

  if (!page.content) return <Page404 />;

  return (
    <LayoutMain>
      <Page>
        <PageTopBar breadcrumbs={page.breadcrumbs} />
        <div
          className={styles.page}
          itemScope
          itemType='http://schema.org/Article'
          data-testid='page-wiki-article'
        >
          <div className={styles.contentContainer}>
            {currentRubric && <div className={styles.currentRubric}>{currentRubric}</div>}
            <div className={styles.navigation}>
              {!isMobileM && (
                <ArticlesNavButton icon={<IconLeft />} article={prevArticle} direction='prev' />
              )}

              <div className={styles.heading}>
                <h1 className={styles.title} itemProp='headline'>
                  {title}
                </h1>
              </div>

              {!isMobileM && (
                <ArticlesNavButton icon={<IconRight />} article={nextArticle} direction='next' />
              )}
            </div>
            {tags.length > 0 && <ButtonTabs tabs={tabs} className={styles.tags} scrollable />}
          </div>
          <div className={styles.content} ref={refGrid} itemProp='articleBody'>
            {content.map((item, index) => {
              switch (item.tag) {
                case 'lead':
                  return (
                    <div
                      className={cn({ [styles.contentContainer]: !isDesktop }, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <Lead contentData={item.content} />
                    </div>
                  );

                case 'grid-vertical':
                  return (
                    <div
                      className={cn(styles.contentContainer, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <TwoColumnsContent contentData={item.content} view='vertical' />
                    </div>
                  );

                case 'grid-horizontal':
                  return (
                    <div
                      className={cn(styles.contentContainer, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <TwoColumnsContent contentData={item.content} view='horizontal' />
                    </div>
                  );

                case 'grid-vertical-text-right':
                  return (
                    <div
                      className={cn(styles.contentContainer, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <SingleContent contentData={item.content} view='vertical' />
                    </div>
                  );

                case 'html':
                  return (
                    <RawHtml
                      className={styles.contentContainer}
                      key={`${id} ${index}`}
                      html={item.content}
                    />
                  );

                case 'instagram-post':
                  if (!item.content) return;

                  return <InstagramPost className={styles.instagramPost} {...item.content} />;

                case 'carousel':
                  return (
                    <ProductsSection
                      className={cn(styles.widgetRow, styles.contentCollage)}
                      title={t('room-tour-title')}
                      products={item.content}
                      analyticsTitle='Товары рум-тура'
                    />
                  );

                case 'video':
                  return (
                    <div
                      className={cn(styles.contentContainer, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <VideoPlayer videoId={item.content} />
                    </div>
                  );

                case 'horizontal-slider':
                  return (
                    <div className={styles.widgetRow} key={`${id} ${index}`}>
                      <FullscreenSlider medias={item.content} />
                    </div>
                  );

                case 'vertical-slider':
                  return (
                    <div className={styles.widgetRow} key={`${id} ${index}`}>
                      <PhotoSlider medias={item.content} />
                    </div>
                  );

                case 'collage':
                  return (
                    <ProductsSection
                      className={cn(styles.widgetRow, styles.contentCollage)}
                      products={item.content.products}
                      analyticsTitle='Блог'
                    />
                  );

                case 'interior-image': {
                  return (
                    <div
                      className={cn(styles.contentContainer, styles.widgetRow)}
                      key={`${id} ${index}`}
                    >
                      <InteriorImageContainer
                        analyticsTitle={title}
                        refGrid={refGrid}
                        contentData={item.content}
                      />
                    </div>
                  );
                }

                default:
                  return null;
              }
            })}
          </div>

          <Subscription className={styles.widgetRow} />

          {relatedArticles.length > 0 && (
            <GallerySection
              className={styles.articles}
              defaultSizes
              title={t('related-title')}
              slides={relatedArticles}
              renderSlide={(article) => <ArticleCard article={article} />}
            />
          )}

          {mainImage && <meta itemProp='image' content={mainImage} />}
          {tags.length > 0 &&
            tags.map((tag) => <meta key={tag.title} itemProp='about' content={tag.title} />)}
        </div>
      </Page>
    </LayoutMain>
  );
};

export default PageWikiArticle;
